import { TagsType } from "@components/Common/HomeworkTemplate/Modals/types";
import { useAuth } from "@contexts/Auth";
import {
  CommonTagBase as CommonTagBaseOwner,
  ownerApi,
} from "@root/endpoints/TimeTactApi/ownerApi";
import {
  CommonTagBase as CommonTagBaseStudent,
  studentApi,
} from "@root/endpoints/TimeTactApi/studentApi";
import {
  CommonTagBase as CommonTagBaseSuperOwner,
  superOwnerApi,
} from "@root/endpoints/TimeTactApi/superOwnerApi";
import {
  CommonTagBase as CommonTagBaseTeacher,
  teacherApi,
} from "@root/endpoints/TimeTactApi/teacherApi";
import { useDebounce } from "ahooks";
import { useCallback, useEffect, useMemo, useState } from "react";

type Props = {
  tagInputValue: TagsType | null;
  handleChange: (value: string, shouldSave?: boolean) => void;
};

type CommonTagBase =
  | CommonTagBaseOwner
  | CommonTagBaseStudent
  | CommonTagBaseSuperOwner
  | CommonTagBaseTeacher;

export const useTagInputService = ({ tagInputValue, handleChange }: Props) => {
  const {
    isCurrentRoles: { isTeacher, isOwner, isStudent },
  } = useAuth();
  const perPageItem = 20;
  const debouncedValue = useDebounce(tagInputValue?.name, { wait: 300 });

  const [searchedTags, setSearchedTags] = useState<CommonTagBase[]>([]);
  const [page, setPage] = useState(1);
  const [previousTotalCount, setPreviousTotalCount] = useState<number>(0);

  const handleGetCommonTagQuery = () => {
    if (isTeacher) return teacherApi.endpoints.getCommonTagsAsTeacher;
    if (isOwner) return ownerApi.endpoints.getCommonTagsAsOwner;
    if (isStudent) return studentApi.endpoints.getCommonTagsAsStudent;
    return superOwnerApi.endpoints.getCommonTagsAsSuperOwner;
  };

  const [getCommonTags, { data: tagsData, isLoading: isLoadingTags }] =
    handleGetCommonTagQuery().useLazyQuery();

  const onChange = (value: string, shouldSave = false) => {
    handleChange(value, shouldSave);
  };

  const hasMore = useMemo(() => {
    if (
      tagsData &&
      searchedTags.length &&
      tagsData.total_count === searchedTags.length
    ) {
      return false;
    }
    if (tagInputValue?.name.length === 0) {
      return true;
    }
    return true;
  }, [tagsData, searchedTags, tagInputValue]);

  const fetchTags = useCallback(() => {
    if (!debouncedValue || !hasMore) {
      return;
    }
    if (
      tagsData &&
      previousTotalCount > 0 &&
      previousTotalCount === tagsData.total_count &&
      previousTotalCount <= perPageItem
    ) {
      return;
    }
    getCommonTags({
      searchType: "name",
      searchWord: debouncedValue,
      page,
      perPage: perPageItem,
    });
  }, [debouncedValue, hasMore, getCommonTags, page]);

  useEffect(() => {
    if (tagsData) {
      setSearchedTags((prevTags) => [
        ...prevTags,
        ...tagsData.common_tags.filter(
          (newTag) =>
            !prevTags.some((existingTag) => existingTag.id === newTag.id),
        ),
      ]);
      setPreviousTotalCount(tagsData.total_count);
    }
  }, [tagsData]);

  useEffect(() => {
    if (tagInputValue?.name.length === 0) {
      setPage(1);
      setSearchedTags([]);
      setPreviousTotalCount(0);
    }
  }, [tagInputValue]);

  useEffect(() => {
    if (debouncedValue?.length && hasMore) {
      fetchTags();
    }
  }, [debouncedValue, hasMore, page]);

  const loadMore = () => {
    if (hasMore && !isLoadingTags) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  const filteredTags = useMemo(
    () =>
      tagInputValue?.name
        ? searchedTags.filter((tag) => tag.name.includes(tagInputValue.name))
        : [],
    [tagInputValue, searchedTags],
  );

  return {
    filteredTags,
    handleChange,
    isLoadingTags,
    onChange,
    loadMore,
    hasMore,
  };
};
